<template>
  <v-dialog
    v-model="dialogQuestionnaire"
    persistent
    max-width="800px"
    scrollable
  >
    <template #activator="{}">
      <v-tooltip
        bottom
        max-width="200px"
        open-delay="750"
      >
        <template #activator="{ on, attrs }">
          <div v-on="on">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="dialogQuestionnaire = true"
            >
              <v-icon>fa-folder-plus</v-icon>
            </v-btn>
          </div>
        </template>
        <span>
          Neues Szenario anlegen 
        </span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col
            cols="auto"
            class="mr-auto"
          >
            <h3>Neues Szenario anlegen</h3>
          </v-col>
          <v-col cols="auto">
            <template v-if="autoOpen">
              <v-btn
                icon
                @click="showUserSettingsDialog"
              >
                <v-icon>fa-user</v-icon>
              </v-btn>
              <v-btn
                icon
                @click="emitPerformLogout"
              >
                <v-icon>fa-sign-out-alt</v-icon>
              </v-btn>
            </template>
            <v-btn
              v-else
              icon
              @click="dialogQuestionnaire = false"
            >
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text
        class="pt-5"
      >
        <v-alert
          v-if="autoOpen"
          type="info"
          elevation="2"
          prominent
          class="mt-3"
          icon="fa-charging-station"
        >
          <b>Herzlich Willkommen bei Quanticy!</b><br>
          Bitte füllen Sie die für die Berechnung des Bedarfs benötigten Daten in diesem Formular aus, um ihr erstes
          <v-tooltip
            bottom
            color="black"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                style="border-bottom: 1px dotted"
                v-on="on"
              >
                Szenario
              </span>
            </template>
            <span>Berechnungsszenario mit variablen Eingangsparametern</span>
          </v-tooltip>
          anzulegen.
          Bis auf die Felder "Stadt/Gemeinde/Landkreis" und "Name für neues Szenario" können alle Werte
          nach dem Anlegen des Szenarios geändert werden, bevor es von Ihnen zur Berechnung freigegeben wird.<br> 
        </v-alert> 
        <v-stepper
          v-model="step"
          vertical
          non-linear
          class="custom-stepper"
        >
          <v-alert
            type="info"
            border="top"
            colored-border
            elevation="2"
            class="ma-3"
          >
            Alle regionalen Daten finden Sie bei Bedarf beim
            <a
              target="_blank"
              href="https://www.wegweiser-kommune.de/"
            >Wegweiser Kommune</a>
            der Bertelsmann Stiftung.
          </v-alert>
          <v-stepper-step
            :color="(Object.keys(updateObj.ags).length > 0)? 'success' : 'error'"
            :complete="Object.keys(updateObj.ags).length > 0"
            step="1"
            editable
            edit-icon="fa-check"
          >
            Stadt/Gemeinde/Landkreis
          </v-stepper-step>

          <v-stepper-content step="1">
            <v-autocomplete 
              v-model="updateObj.ags"
              :items="localAgsList"
              label="Stadt/Gemeinde/Landkreis"
              item-text="name"
              return-object
            />
          </v-stepper-content>
        
          <v-stepper-step
            :color="updateObj.update.description.length > 0 ? 'success' : 'error'"
            :complete="updateObj.update.description.length > 0"
            step="2"
            editable
            edit-icon="fa-check"
          >
            Name für neues Szenario
          </v-stepper-step>
          <v-stepper-content step="2">
            <v-text-field
              v-model="updateObj.update.description"
              counter
              maxlength="50"
              label="Name für neues Szenario"
            />
          </v-stepper-content>
          <template v-for="(localCiv, i) in updateObj.localCivilization">
            <v-stepper-step
              :key="`step${i}`"
              :color="(parseInt(localCiv[localCiv.attr]) >= 0)? 'success' : 'error'"
              :complete="parseInt(localCiv[localCiv.attr]) >= 0"
              :step="i + 3"
              editable
              edit-icon="fa-check"
            >
              {{ localCiv.label }}
            </v-stepper-step>
            <v-stepper-content
              :key="`step-content${i}`"
              :step="i + 3"
            >
              <v-text-field
                v-model="localCiv[localCiv.attr]"
                :label="localCiv.label"
                :rules="editLocalCivilizationRules"
              />
            </v-stepper-content>
          </template> 
        </v-stepper>
      
        <v-card class="mt-4">
          <v-card-text> 
            <edit-global-civilization 
              v-model="updateObj.globalCivilization"
              show-default-value-alert
              :global-civilizations-original="globalCivilizationOriginal"
              :loaded="loaded"
              :changed="globalCivilizationChanged"
              :mode="mode"
              @reset="resetGlobalCivilizationValues"
            />
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-divider />
      <v-card-actions class="d-flex flex-column justify-center">
        <v-btn
          class="my-3"
          color="primary"
          :loading="saving"
          :disabled="!updateObjComplete || saving"
          @click="complete()"
        >
          Neues Szenario Anlegen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import agsList from '@/assets/agsList'
import EditGlobalCivilization from '@/components/dialogs/updateSettingsDialog/EditGlobalCivilization'
import { mapGetters } from "vuex";

export default {
  name: 'Questionnaire',
  components: {"edit-global-civilization": EditGlobalCivilization},
  props: {
    autoOpen: Boolean
  },
  data: () => ({
    dialogQuestionnaire: false,
    step: 1,

    // clones to keep track of original state
    globalCivilizationOriginal: [
      {"year":2023,"eCarFraction":"0.051"},
      {"year":2025,"eCarFraction":"0.0799"},
      {"year":2028,"eCarFraction":"0.1295"},
      {"year":2030,"eCarFraction":"0.1611"}
    ],
    loaded: true,
    saving: false,
    mode: "edit",

    localAgsList: agsList,

    updateObj: {
      ags: {},
      update: {description: ""},
      localCivilization: [
        {
          attr: "numberOfDCVehicles",
          localCivilizationName: "transit_traffic_per_day_highway",
          numberOfDCVehicles: null, 
          label: "Transit auf der/den Bundesstraße/n pro Tag"
        },
        {
          attr: "numberOfDCVehicles",
          localCivilizationName: "transit_traffic_per_day_state_highway",
          numberOfDCVehicles: null,
          label: "Transit auf der/den Autobahn/en pro Tag"
          
        },
        {
          attr: "numberOfPersons",
          localCivilizationName: "employee_contributing_social_security",
          numberOfPersons: null,
          label: "Anzahl an Sozialversicherungspflichtigen"
        },
        {
          attr: "numberOfPersons",
          localCivilizationName: "hotel_guests_per_day",
          numberOfPersons: null,
          label: "Hotelgäste pro Tag"
        },
        {
          attr: "numberOfPersons",
          localCivilizationName: "event_persons_per_day",
          numberOfPersons: null,
          label: "Veranstaltungsbesucher pro Tag"
        },
        {
          attr: "numberOfVehicles",
          localCivilizationName: "commercial vehicles",
          numberOfVehicles: null,
          label: "Anzahl gewerblicher Fahrzeuge"
        },
        {
          attr: "numberOfPersons",
          localCivilizationName: "number_of_citizen_above_18",
          numberOfPersons: null,
          label: "Anzahl der Bewohner über 18"
        }
      ],
      globalCivilization: [
        {"year":2023,"eCarFraction":"0.051"},
        {"year":2025,"eCarFraction":"0.0799"},
        {"year":2028,"eCarFraction":"0.1295"},
        {"year":2030,"eCarFraction":"0.1611"}
      ]
    }
  }),
  computed: {
    ...mapGetters(['metadata', 'user']),
    // whether globalCivilization has been modified and is != globalCivilizationOriginal
    globalCivilizationChanged() {
      return this.civilizationArrayHasChanged(
        this.updateObj.globalCivilization,
        this.globalCivilizationOriginal
      );
    },
    updateObjComplete() {
      return !this.updateObj.localCivilization.some(elem => !(/^\d+(\.\d+)?$/.test(elem[elem.attr]))) &&
        Object.keys(this.updateObj.ags).length > 0 &&
        this.updateObj.update.description.length > 0 &&
        this.updateObj.globalCivilization.length > 0
    },
    editLocalCivilizationRules() {
      return [
        input =>
          /^\d+(\.\d+)?$/.test(input) ||
          "Anzahl muss als positive Ganz- oder Dezimalzahl angegeben werden"
      ];
    }
  },
  watch: {
    // automatically open this dialog if there are no ags for this client
    autoOpen(newVal) {
      if (newVal) {
        this.dialogQuestionnaire = true;
      }
    },
    // reset auto open value in Main.vue
    dialogQuestionnaire(newVal) {
      if (this.autoOpen && !newVal) {
        this.$emit("resetAutoOpen");
      }
    }
  },
  methods: {
    complete(){
      this.saving = true;
      $api.post(`update/new?client_id=${this.user.clientId}`, this.updateObj).then(() => {
        window.EventBus.$emit("reloadUpdates");
        window.EventBus.$emit("reloadAgs"); 
        if(
          !Object.prototype.hasOwnProperty.call(
            this.metadata,
            "shepherd"
          ) ||
          this.metadata.shepherd?.show 
        ){
          window.EventBus.$emit("runShepherd"); 
        }
        this.dialogQuestionnaire = false;
        this.saving = false;
      }).catch((e) => {
        console.log(e);
        console.debug(e);
        this.saving = false;
      });
    },

    resetGlobalCivilizationValues() {
      this.updateObj.globalCivilization = JSON.parse(
        JSON.stringify(this.globalCivilizationOriginal)
      );
    },

    civilizationArrayHasChanged(values, originalValues) {
      let currentValues = JSON.stringify(
        this.getCleanedCivilizationArray(values)
      );
      return originalValues && currentValues != JSON.stringify(originalValues);
    },

    getCleanedCivilizationArray(values) {
      // create copy of value array...
      return JSON.parse(JSON.stringify(values)).reduce((acc, elem) => {
        // ...without the attributes that are added by child component
        delete elem.editing;
        delete elem.changed;
        acc.push(elem);
        return acc;
      }, []);
    },

    emitPerformLogout() {
      window.EventBus.$emit("performLogout");
    },

    showUserSettingsDialog() {
      window.EventBus.$emit("showUserSettingsDialog");

    }
  }
}
</script>

<style>
.custom-stepper .v-stepper__step__step .v-icon.v-icon {
    font-size: 0.9rem;
}
</style>
